/**
 * Last line of defence for a route chunk that will not load.
 *
 * ./routes/lazyRoute.ts already retries and, for a chunk that has vanished
 * (the usual cause: a deploy replaced the hashed files while this tab was
 * open), reloads once. If the user lands here, that recovery has already been
 * spent — so offer the action rather than a dead screen, which is what the
 * previous behaviour amounted to.
 *
 * Only chunk-loading failures are handled. Anything else is re-thrown so the
 * surrounding Sentry.ErrorBoundary still reports it.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { isChunkLoadError } from "../routes/lazyRoute";

type Props = { children: ReactNode };
type State = { chunkError: Error | null; rethrow: Error | null };

const Rethrow: React.FC<{ error: Error }> = ({ error }) => {
  throw error;
};

class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { chunkError: null, rethrow: null };

  static getDerivedStateFromError(error: Error): State {
    return isChunkLoadError(error)
      ? { chunkError: error, rethrow: null }
      : { chunkError: null, rethrow: error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      console.error("[App2Food] route chunk failed to load", error, info);
    }
  }

  private handleReload = (): void => {
    try {
      // Clear the one-reload guard so this attempt is allowed to reload again.
      window.sessionStorage.removeItem("a2f:chunk-reloaded");
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  render(): ReactNode {
    const { chunkError, rethrow } = this.state;

    if (rethrow) return <Rethrow error={rethrow} />;

    if (chunkError) {
      return (
        <div className="text-center p-4" style={{ minHeight: "50vh" }}>
          <h5 className="mb-2">This page didn't finish loading</h5>
          <p className="text-muted mb-3">
            It's usually a brief connection problem, or a new version of the
            menu being published while this page was open.
          </p>
          <button type="button" className="btn btn-primary" onClick={this.handleReload}>
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
